import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import cx from 'classnames';

import { GlossIcon } from 'glossDS/Icons/GlossIcon/GlossIcon';

import { SubNavItem } from 'hooks/navigation/useNavigation';

import { Arrow } from '../Arrow/Arrow';

import s from './PlayfulNavigation.module.css';

type NavItemProps = {
  children: React.ReactNode;
  highlighted?: boolean;
  href?: string;
  newTab?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
  subNavItems?: SubNavItem[];
} & (
  | { subNavItems: SubNavItem[]; href?: never }
  | { subNavItems?: never; href: string }
);

export const PlayfulNavItem = ({
  children,
  highlighted,
  href,
  newTab,
  onClick,
  ariaLabel,
  subNavItems,
}: NavItemProps) => {
  const { route } = useRouter();

  const isCurrent = href ? route === href : false;

  const linkClassNames = cx(s.navItem, { [s.highlighted]: highlighted });

  const linkContent = (
    <>
      {children}
      {isCurrent ? (
        <span className={s.navItemCurrentA11yHidden}>
          {' -  Current location'}
        </span>
      ) : null}
      {isCurrent || highlighted ? (
        <div className={s.activeArrow}>
          <Arrow />
        </div>
      ) : null}
    </>
  );

  if (subNavItems) {
    return (
      <div className={cx(s.hasSubNav, { [s.highlighted]: highlighted })}>
        <span
          role="button"
          tabIndex={0}
          className={cx(s.navItem, linkClassNames)}
        >
          {linkContent}
          <GlossIcon
            icon="arrow-down-s-line"
            className={s.subNavArrow}
            size={24}
          />
        </span>
        <ul className={s.subNav}>
          {subNavItems.map((item) => (
            <li key={item.to} className={s.subNavItem}>
              <Link href={item.to} onClick={item.onClick} className={s.navItem}>
                {item.label}
                {route === item.to ? (
                  <span className={s.navItemCurrentA11yHidden}>
                    {' -  Current location'}
                  </span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (newTab) {
    return (
      <a
        href={href}
        className={linkClassNames}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        {...(ariaLabel ? { ariaLabel } : {})}
      >
        {linkContent}
      </a>
    );
  }

  return (
    <Link
      href={href}
      onClick={onClick}
      className={linkClassNames}
      aria-label={ariaLabel}
    >
      {linkContent}
    </Link>
  );
};
